import { CoachCard } from '@efdigital/ll-addon-coaching';
import {
  isCancelled,
  isHistory,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-lifecycle';
import type {
  CoachingPartnership,
  CoachingPlan,
  CoachingSession,
  CoachProfile,
  Cohort,
} from '@efdigital/ll-addon-data';
import type { LookupUser } from '@efdigital/ll-admin-v2/services/user-lookup';
import type UserLookupService from '@efdigital/ll-admin-v2/services/user-lookup';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import { CoacheeCard } from '../coachee-card.gts';
import SessionCalendar from './detail/session-calendar.gts';
import SessionCancellation from './detail/session-cancellation.gts';
import SessionInfo from './detail/session-info.gts';
import SessionParticipation from './detail/session-participation.gts';
import { SessionPrimaryAction } from './detail/session-primary-action.gts';

interface CoachingSessionDetailSignature {
  Args: {
    coachingSession: CoachingSession;
    coachProfile?: CoachProfile;
    coachingPlan: CoachingPlan;
    coachingPartnership: CoachingPartnership;
    cohort?: Cohort;
    refresh: () => Promise<void>;
    abandoned: () => Promise<void>;
  };
  Element: HTMLDivElement;
}

export class CoachingSessionDetail extends Component<CoachingSessionDetailSignature> {
  @service declare store: Store;
  @service declare userLookup: UserLookupService;

  lookupUsers = async () => {
    await this.userLookup.lookupUsers([
      this.args.coachingSession.coachUserId,
      this.args.coachingSession.user,
    ]);
  };

  get coach(): LookupUser {
    return this.userLookup.fromCache(this.args.coachingSession.coachUserId);
  }

  get coachee(): LookupUser {
    return this.userLookup.fromCache(this.args.coachingSession.user);
  }

  /**
   * Scopes for the shared sessions list, so a session can be used as a jumping
   * off point to everything around it rather than a dead end.
   */
  get coachSessionsQuery() {
    return { coachUserId: this.args.coachingSession.coachUserId };
  }

  get coacheeSessionsQuery() {
    return { coacheeUserId: this.args.coachingSession.user };
  }

  /** A partnership's coach and coachee never change, so scope by both rather than partnershipId. */
  get partnershipSessionsQuery() {
    return {
      coachUserId: this.args.coachingSession.coachUserId,
      coacheeUserId: this.args.coachingSession.user,
    };
  }

  <template>
    <Await @promise={{(this.lookupUsers)}}>
      <:success>
        <div class="row g-4 mb-4">
          <div class="col-12 col-xl-4">
            <SessionInfo
              @coachingSession={{@coachingSession}}
              @cohort={{@cohort}}
              class="h-100"
            >
              <:actions>
                <SessionPrimaryAction
                  @coachingSession={{@coachingSession}}
                  @onSuccess={{@refresh}}
                  @onAbandoned={{@abandoned}}
                />
              </:actions>
            </SessionInfo>
          </div>

          <div class="col-12 col-lg-6 col-xl-4">
            <CoacheeCard
              @coachee={{this.coachee}}
              @coachingPlan={{@coachingPlan}}
            />
          </div>
          <div class="col-12 col-lg-6 col-xl-4">
            {{#if @coachProfile}}
              <CoachCard @coach={{@coachProfile}} />
            {{else}}
              <div class="card h-100">
                <div class="card-body">
                  <p class="mb-0">No coach profile found</p>
                </div>
              </div>
            {{/if}}
          </div>
        </div>

        <nav class="d-flex flex-wrap align-items-center gap-3 mb-4">
          <span class="gud-eyebrow mb-0">Related</span>
          <LinkTo
            @route="coaching.coaches.coach"
            @model={{@coachingSession.coachUserId}}
            class="small"
          >Coach profile</LinkTo>
          <LinkTo
            @route="coaching.sessions"
            @query={{this.coachSessionsQuery}}
            class="small"
          >All sessions for this coach</LinkTo>
          <LinkTo
            @route="coaching.sessions"
            @query={{this.coacheeSessionsQuery}}
            class="small"
          >All sessions for this coachee</LinkTo>
          {{#if @coachingPartnership.id}}
            <LinkTo
              @route="coaching.sessions"
              @query={{this.partnershipSessionsQuery}}
              class="small"
            >This partnership&#39;s sessions</LinkTo>
          {{/if}}
        </nav>

        {{#if (isCancelled @coachingSession.status)}}
          <SessionCancellation
            @coachingSession={{@coachingSession}}
            class="mb-4"
          />
        {{else if (isHistory @coachingSession.status)}}
          <SessionParticipation
            @coachingSession={{@coachingSession}}
            @coachee={{this.coachee}}
            @coach={{this.coach}}
            @onSuccess={{@refresh}}
            class="mb-4"
          />
        {{/if}}

        {{#if @coachingSession.bookingFailureStage}}
          <SessionCalendar @coachingSession={{@coachingSession}} class="mb-4" />
        {{/if}}
      </:success>
    </Await>
  </template>
}
